import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Profile, Role } from '../models';
import { getProfile, listProfiles } from '../services/directoryService';

/**
 * Active-role context.
 *
 * During the foundation sprint we don't have real auth — instead the user
 * picks a Profile from the demo dataset via the topbar role switcher. Every
 * route reads from this hook so role-aware UI works everywhere.
 *
 * When real auth lands, replace `setActiveProfileId` with the auth callback;
 * the rest of the app keeps working unchanged.
 */

const STORAGE_KEY = 'thescorekeeper.activeProfileId';

interface RoleContextValue {
  profile: Profile;
  role: Role;
  /** All available profiles for the dev role-switcher dropdown. */
  allProfiles: Profile[];
  setActiveProfileId: (id: string) => void;
  setRole: (role: Role) => void;
}

const RoleContext = createContext<RoleContextValue | null>(null);

const FALLBACK_PROFILE: Profile = {
  id: 'profile-viewer',
  kind: 'profile',
  createdAt: '2026-01-01T00:00:00.000Z',
  updatedAt: '2026-01-01T00:00:00.000Z',
  displayName: 'Viewer',
  defaultRole: 'fan',
  globalRoles: ['fan'],
  directoryStatus: 'active',
  preferredContactMethod: 'none',
  contactPreferences: {},
  attributes: {},
  source: 'system',
  tags: [],
};

export function RoleProvider({ children }: { children: ReactNode }) {
  const allProfiles = useMemo(() => listProfiles(), []);
  const initial =
    (typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY)) ||
    allProfiles[0]?.id;

  const [activeProfileId, setActiveProfileIdState] = useState<string>(initial ?? '');
  const profile = useMemo(
    () => getProfile(activeProfileId) ?? allProfiles[0] ?? FALLBACK_PROFILE,
    [activeProfileId, allProfiles],
  );
  const [role, setRole] = useState<Role>(profile?.defaultRole ?? 'fan');

  // Reset role when profile changes so the simulation matches the persona.
  useEffect(() => {
    if (profile) setRole(profile.defaultRole);
  }, [profile?.id]);

  // Persist the chosen profile so dev reloads keep their seat.
  useEffect(() => {
    if (typeof window !== 'undefined' && activeProfileId) {
      window.localStorage.setItem(STORAGE_KEY, activeProfileId);
    }
  }, [activeProfileId]);

  const value: RoleContextValue = {
    profile,
    role,
    allProfiles,
    setActiveProfileId: (id) => setActiveProfileIdState(id),
    setRole,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useActiveRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useActiveRole must be used inside <RoleProvider>');
  return ctx;
}
